export type CallDetail = { _id: string };

type Supplier = { callDetails?: CallDetail[] };
type Area = { suppliers?: Supplier[] };
export type CachedProject = { area?: Area[] };

/**
 * Swap one supplier's cached callDetails for the array an edit or delete route returned.
 *
 * The owning supplier is located by the entry id alone — the same "one globally unique id is
 * enough" rule the routes follow — so callers don't have to thread areaId/supplierId back down.
 * Returns the project by reference when the entry isn't cached, so callers can skip the re-render.
 */
export function replaceCallDetails<T extends CachedProject>(
  project: T,
  callDetailId: string,
  callDetails: CallDetail[],
): T {
  if (!project?.area || !callDetailId || !callDetails) return project;

  const holdsEntry = (supplier: Supplier) =>
    !!supplier.callDetails?.some((entry) => entry._id === callDetailId);

  const ownsEntry = (area: Area) => !!area.suppliers?.some(holdsEntry);

  if (!project.area.some(ownsEntry)) return project;

  return {
    ...project,
    area: project.area.map((area) =>
      ownsEntry(area)
        ? {
            ...area,
            suppliers: area.suppliers!.map((supplier) =>
              holdsEntry(supplier) ? { ...supplier, callDetails } : supplier,
            ),
          }
        : area,
    ),
  } as T;
}
